// Types for Notifications used across the Notifications feature

// Notification Event for multi-notifications
export interface NotificationEvent {
  event_id: string;
  notification_type: string;
  created: string;
  content: string;
  source_data?: {
    message_id?: number;
    attachments?: {
      has_attachments: boolean;
      attachment_count: number;
    };
    author_name?: string;
    [key: string]: unknown;
  };
}

// Notification Types
export interface Notification {
  id: number;
  notification_type: string;
  notification_source?: NotificationSource;
  source?: NotificationSource;
  content: string;
  created: string;
  created_at?: string;
  is_read: boolean;
  read_at?: string;
  url?: string;
  is_watched?: boolean;
  is_single_notification?: boolean;
  is_bulk_notification?: boolean;
  is_multi_notification?: boolean;
  is_property_notification?: boolean;
  is_system_notification?: boolean;
  notification_type_category?: string;
  consolidated_count?: number;
  related_objects?: {
    type?:
      | 'single_certification'
      | 'bulk_certifications'
      | 'property'
      | 'system'
      | 'consolidated_events'
      | 'recert_tracking'
      | 'recert_tracking_group';
    source_type?: string;
    total_count?: number;
    action_type?: string;
    message_id?: number;
    attachments?: {
      has_attachments: boolean;
      attachment_count: number;
    };

    // Recert: single (recert_tracking) or frontend-only group (recert_tracking_group)
    tracking_id?: number;
    certification_name?: string;
    timeframe_label?: string;
    property_id?: number;
    property_name?: string;
    deep_link?: string;
    items?: Array<{
      tracking_id?: number;
      certification_name: string;
      timeframe_label: string;
      needs_creation?: boolean;
      needs_invite?: boolean;
    }>;
    /** Frontend-only: notification ids in this group (for mark-as-read) */
    _groupedIds?: number[];

    // Multi-notification specific fields
    certification_id?: number;
    notification_type?: string;
    consolidated_count?: number;
    consolidation_window_start?: string;
    consolidation_window_end?: string;
    events?: NotificationEvent[];
    last_consolidated_at?: string;
    was_read_before_consolidation?: boolean;
  };
  certifications?: {
    id: number;
    display_name: string;
    effective_date: string;
    deadline: string;
    property?: {
      id: number;
      name: string;
      slug: string;
      enable_renter_notifications?: boolean;
    };
    household?: {
      id: number;
      name: string;
    };
  }[];
  // Optional single objects for backward/legacy support
  certification?: {
    id: number;
    display_name?: string;
    effective_date?: string;
    deadline?: string;
    property?: {
      id?: number;
      name?: string;
      slug?: string;
      enable_renter_notifications?: boolean;
    };
    household?: {
      id?: number;
      name?: string;
    };
  };
  properties?: {
    id: number;
    name: string;
  }[];
  // Optional single property for backward/legacy support
  property?: {
    id?: number;
    name?: string;
    slug?: string;
    enable_renter_notifications?: boolean;
  };
}

export interface NotificationBellProps {
  iconSize?: number;
  isOpen?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  notificationSourceFilter?: NotificationSource;
}

export interface NotificationRowProps {
  notification: Notification;
  onMarkAsRead: (notifId: number, e: unknown) => Promise<void>;
  onMarkAsUnread: (notifId: number, e: unknown) => Promise<void>;
  onNotificationClick: (notification: Notification) => Promise<void>;
  showReadStatus?: boolean;
  showTimestamp?: boolean;
  showActions?: boolean;
  compact?: boolean;
}

// Notification type categories
export type NotificationType =
  | 'email'
  | 'documents'
  | 'file_owner'
  | 'sms'
  | 'fax'
  | 'status_note'
  | 'bulk_file_owner_assignment'
  | 'bulk_status_change'
  | 'bulk_property_transfer';

export type NotificationSource =
  | 'file_owner'
  | 'watched'
  | 'property'
  | 'system'
  | 'renter';

// Notification settings types
export interface NotificationSettings {
  // Renter notifications
  receive_renter_notifications?: boolean;
  receive_renter_notifications_email?: boolean;

  // File Owner notifications
  receive_new_email_notifications?: boolean;
  receive_new_documents_notifications?: boolean;
  receive_new_file_owner_notifications?: boolean;
  receive_new_sms_notifications?: boolean;
  receive_new_fax_notifications?: boolean;
  receive_new_status_note_notifications?: boolean;

  // Watched notifications
  watched_receive_new_email_notifications?: boolean;
  watched_receive_new_documents_notifications?: boolean;
  watched_receive_new_file_owner_notifications?: boolean;
  watched_receive_new_sms_notifications?: boolean;
  watched_receive_new_fax_notifications?: boolean;
  watched_receive_new_status_note_notifications?: boolean;
  watched_receive_checklist_item_cert_notifications?: boolean;

  // Property notifications
  receive_property_email_notifications?: boolean;
  receive_property_documents_notifications?: boolean;
  receive_property_file_owner_notifications?: boolean;
  receive_property_sms_notifications?: boolean;
  receive_property_fax_notifications?: boolean;
  receive_property_status_note_notifications?: boolean;
  receive_recert_notices_reminder?: boolean;
  receive_new_certifications_notifications?: boolean;
  receive_checklist_item_cert_notifications?: boolean;
  receive_checklist_item_property_notifications?: boolean;

  // Email settings
  receive_new_email_notifications_email?: boolean;
  receive_new_documents_notifications_email?: boolean;
  receive_new_file_owner_notifications_email?: boolean;
  receive_new_sms_notifications_email?: boolean;
  receive_new_fax_notifications_email?: boolean;
  receive_new_status_note_notifications_email?: boolean;

  watched_receive_new_email_notifications_email?: boolean;
  watched_receive_new_documents_notifications_email?: boolean;
  watched_receive_new_file_owner_notifications_email?: boolean;
  watched_receive_new_sms_notifications_email?: boolean;
  watched_receive_new_fax_notifications_email?: boolean;
  watched_receive_new_status_note_notifications_email?: boolean;
  watched_receive_checklist_item_cert_notifications_email?: boolean;

  receive_property_email_notifications_email?: boolean;
  receive_property_documents_notifications_email?: boolean;
  receive_property_file_owner_notifications_email?: boolean;
  receive_property_sms_notifications_email?: boolean;
  receive_property_fax_notifications_email?: boolean;
  receive_property_status_note_notifications_email?: boolean;
  receive_recert_notices_reminder_email?: boolean;
  receive_new_certifications_notifications_email?: boolean;
  receive_checklist_item_cert_notifications_email?: boolean;
  receive_checklist_item_property_notifications_email?: boolean;
}

// Helper type guards
export const isMultiNotification = (notification: Notification): boolean => {
  return notification.related_objects?.type === 'consolidated_events';
};

export const isSingleNotification = (notification: Notification): boolean => {
  return notification.related_objects?.type === 'single_certification';
};

export const isBulkNotification = (notification: Notification): boolean => {
  return notification.related_objects?.type === 'bulk_certifications';
};

export const isPropertyNotification = (notification: Notification): boolean => {
  return notification.related_objects?.type === 'property';
};

export const isSystemNotification = (notification: Notification): boolean => {
  return notification.related_objects?.type === 'system';
};
